import { useState } from 'react';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';

import { useIsLoggedIn, useSetIsLoggedIn } from '@zoopla/user';
import { TrackedProperty } from '../../graphql.generated';

import { useTrackPropertyContext } from '../context/track-property-context';
import type { PropertyDataForAnalytics } from '@zoopla/analytics/property-data';

import { recordTrackOrUntrackProperty } from '../tracking';

export const useTrackProperty = (
    analyticsData: PropertyDataForAnalytics,
    trackedProperty: TrackedProperty,
    isRelationshipModalShowing: boolean,
    updateIsRelationshipModalShowing: (open: boolean) => void,
    consent?: {
        consentText: string;
        consentId: string;
    }
) => {
    const {
        dispatch,
        state: { isTracked }
    } = useTrackPropertyContext();
    const isLoggedIn = useIsLoggedIn();
    const setLoggedIn = useSetIsLoggedIn();

    const SAVE_TRACKED_PROPERTY = gql`
        mutation saveTrackedProperty($trackedProperty: TrackedPropertyInput!) {
            saveTrackedProperty(trackedProperty: $trackedProperty)
        }
    `;

    const DELETE_TRACKED_PROPERTY = gql`
        mutation deleteTrackedProperty($uprn: String) {
            deleteTrackedProperty(uprn: $uprn)
        }
    `;

    const [saveTrackedProperty, { loading: saveLoading, error: saveError }] =
        useMutation(SAVE_TRACKED_PROPERTY);

    const [
        deleteTrackedProperty,
        { loading: deleteLoading, error: deleteError }
    ] = useMutation(DELETE_TRACKED_PROPERTY);

    const [isAuthModalShowing, updateIsAuthModalShowing] = useState(false);

    const { uprn } = analyticsData;

    const handleTrackButtonClick = () => {
        if (saveLoading || deleteLoading) return;
        if (!isLoggedIn) {
            updateIsAuthModalShowing(true);
            return;
        }
        trackOrUntrackProperty();
    };
    const trackOrUntrackProperty = async () => {
        const isCurrentlyTracked = isTracked;
        setLoggedIn(true);
        try {
            if (isCurrentlyTracked) {
                await deleteTrackedProperty({
                    variables: {
                        uprn
                    }
                });
                dispatch({ type: 'untrackProperty' });
            } else {
                updateIsRelationshipModalShowing(true);
                await saveTrackedProperty({
                    variables: {
                        trackedProperty: {
                            uprn,
                            ...consent
                        }
                    }
                });
            }

            recordTrackOrUntrackProperty(
                analyticsData,
                isCurrentlyTracked,
                trackedProperty?.relationship
            );
        } catch (error) {
            updateIsRelationshipModalShowing(false);
            dispatch({ type: 'error' });
        }
    };

    return {
        isTracked,
        handleTrackButtonClick,
        trackOrUntrackProperty,
        isAuthModalShowing,
        updateIsAuthModalShowing,
        isRelationshipModalShowing,
        loading: saveLoading || deleteLoading,
        error: saveError || deleteError
    };
};
